import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { LoincTerm, LoincSearchParams, LoincSearchResult } from '../../types/loinc';
import { loincAPI } from '../../services/api/loincAPI';

interface LoincState {
  terms: LoincTerm[];
  searchResults: LoincSearchResult | null;
  selectedTerms: LoincTerm[];
  recentTerms: LoincTerm[]; // For preserving recent searches
  isLoading: boolean;
  error: string | null;
  lastSearchParams: LoincSearchParams | null;
  language: 'es' | 'en'; // Language support for bilingual LOINC
  searchQuery: string; // Current search query
}

const initialState: LoincState = {
  terms: [],
  searchResults: null,
  selectedTerms: [],
  recentTerms: [],
  isLoading: false,
  error: null,
  lastSearchParams: null,
  language: 'es', // Default to Spanish
  searchQuery: '',
};

export const searchLoincTerms = createAsyncThunk(
  'loinc/search',
  async (params: LoincSearchParams, { rejectWithValue }) => {
    try {
      const result = await loincAPI.searchTerms(params);
      return { result, params };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Search failed');
    }
  }
);

export const getLoincStats = createAsyncThunk(
  'loinc/getStats',
  async (_, { rejectWithValue }) => {
    try {
      const stats = await loincAPI.getStats();
      return stats;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to get stats');
    }
  }
);

const loincSlice = createSlice({
  name: 'loinc',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    addSelectedTerm: (state, action: PayloadAction<LoincTerm>) => {
      const exists = state.selectedTerms.find(term => term.loinc_num === action.payload.loinc_num);
      if (!exists) {
        state.selectedTerms.push(action.payload);
        // Add to recent terms for quick access
        state.recentTerms = [action.payload, ...state.recentTerms.filter(t => t.loinc_num !== action.payload.loinc_num)].slice(0, 10);
      }
    },
    removeSelectedTerm: (state, action: PayloadAction<string>) => {
      state.selectedTerms = state.selectedTerms.filter(term => term.loinc_num !== action.payload);
    },
    clearSelectedTerms: (state) => {
      state.selectedTerms = [];
    },
    clearSearchResults: (state) => {
      state.searchResults = null;
      state.lastSearchParams = null;
      state.searchQuery = '';
    },
    // Language management for bilingual LOINC support
    setLanguage: (state, action: PayloadAction<'es' | 'en'>) => {
      state.language = action.payload;
    },
    // Search query management
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    // Recent terms management
    addRecentTerm: (state, action: PayloadAction<LoincTerm>) => {
      state.recentTerms = [action.payload, ...state.recentTerms.filter(t => t.loinc_num !== action.payload.loinc_num)].slice(0, 10);
    },
    clearRecentTerms: (state) => {
      state.recentTerms = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Search LOINC terms
      .addCase(searchLoincTerms.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(searchLoincTerms.fulfilled, (state, action) => {
        state.isLoading = false;
        state.searchResults = action.payload.result;
        state.lastSearchParams = action.payload.params;
        // If this is a new search (offset 0), replace terms array
        if (action.payload.params.offset === 0) {
          state.terms = action.payload.result.terms;
        } else {
          // Otherwise, append to existing terms (pagination)
          state.terms = [...state.terms, ...action.payload.result.terms];
        }
      })
      .addCase(searchLoincTerms.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Get LOINC stats
      .addCase(getLoincStats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getLoincStats.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(getLoincStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  addSelectedTerm,
  removeSelectedTerm,
  clearSelectedTerms,
  clearSearchResults,
  setLanguage,
  setSearchQuery,
  addRecentTerm,
  clearRecentTerms,
} = loincSlice.actions;

export default loincSlice.reducer;
